import HeaderBlock from "~/modules/pageBlocks/components/blocks/marketing/header/HeaderBlock";
import { useTranslation } from "react-i18next";
import { json, LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { getTranslations } from "~/locale/i18next.server";
import FooterBlock from "~/modules/pageBlocks/components/blocks/marketing/footer/FooterBlock";
import CookieConsentSettings from "~/components/cookies/CookieConsentSettings";
import { useState } from "react";
import OpenModal from "~/components/ui/modals/OpenModal";
import { getLinkTags } from "~/modules/pageBlocks/services/.server/pagesService";

export const meta: MetaFunction<typeof loader> = ({ data }) => data?.metatags || [];
export const loader = async ({ request }: LoaderFunctionArgs) => {
  const { t } = await getTranslations(request);

  return json({
    metatags: [{ title: `${t("front.privacy.title")} | ${process.env.APP_NAME}` }, ...getLinkTags(request)],
  });
};

export default function PrivacyPolicyRoute() {
  const { t } = useTranslation();
  const [showCookieSettingsModal, setShowCookieSettingsModal] = useState(false);
  return (
    <div>
      <div>
        <HeaderBlock />

        {showCookieSettingsModal && (
          <OpenModal onClose={() => setShowCookieSettingsModal(false)}>
            <CookieConsentSettings onUpdated={() => setShowCookieSettingsModal(false)} />
          </OpenModal>
        )}

        <div className="min-h-screen py-6">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <div className="sm:align-center space-y-4 sm:flex sm:flex-col">
              <div className="prose dark:prose-invert text-muted-foreground mx-auto">
                <h1 className="flex justify-center text-3xl font-extrabold tracking-tight sm:text-4xl">{t("front.privacy.title")}</h1>
                <button
                  type="button"
                  onClick={() => setShowCookieSettingsModal(true)}
                  className="text-theme-500 hover:text-theme-600 dark:hover:text-theme-400 underline"
                >
                  Cookie Settings
                </button>
                <p>
                  Your privacy is important to us at DataLang. We are committed to respecting your privacy and complying with applicable laws and regulations to
                  ensure that the personal information you give us is kept appropriately protected and handled responsibly.
                </p>
                <p>This policy is effective as of 14th June 2023 and was last updated on 14th June 2023.</p>
                <h3 className="">Information We Collect</h3>
                <p>
                  The information we collect includes both information you actively give us when interacting with our service, and information that is
                  automatically sent by your devices in the course of using our services.
                </p>
                <p>
                  To use our services, you will need to provide a data source connection string. DataLang will then fetch all the schema tables and columns from
                  your database. This information, along with your queries, is used to generate SQL queries which are executed in your database. Your database
                  credentials are encrypted before they are stored in our database and are decrypted just before executing any SQL queries.
                </p>
                <h4 className="">Log Data</h4>
                <p>
                  When you visit our website, our servers may automatically log the standard data provided by your web browser. This may include your device’s
                  Internet Protocol (IP) address, your browser type and version, the pages you visit, the time and date of your visit, the time spent on each
                  page, other details about your visit, and technical details that occur in conjunction with any errors you may encounter.
                </p>
                <p>
                  Please be aware that while this information may not be personally identifying by itself, it may be possible to combine it with other data to
                  personally identify individual persons.
                </p>
                <h4 className="">Collection and Use of Information</h4>
                <p>We may collect personal information from you when you do any of the following on our website:</p>
                <ul>
                  <li>Provide us with your database connection string for the use of our services</li>
                  <li>Ask a question or provide feedback</li>
                  <li>Sign up for updates from us</li>
                </ul>
                <p>
                  We may collect, hold, use, and disclose information for the following purposes, and personal information will not be further processed in a
                  manner that is incompatible with these purposes:
                </p>
                <ul>
                  <li>Providing and improving our services</li>
                  <li>Communicating with you</li>
                  <li>Security and fraud prevention</li>
                  <li>Compliance with legal obligations</li>
                </ul>
                <h4 id="security-of-your-personal-information" className="">
                  <a href="#security-of-your-personal-information">Security of Your Personal Information</a>
                </h4>
                <p>
                  We are committed to ensuring the security of the personal information we collect and hold. We take all reasonable steps to protect your
                  personal information from misuse, interference, loss, and unauthorized access, modification, or disclosure.
                </p>
                <p>
                  Your connection string details are stored securely in our database in encrypted form. We use commercially acceptable means to protect it, but
                  remember that no method of electronic storage is 100% secure and we cannot guarantee its absolute security.
                </p>
                <h3 id="childrens-privacy" className="">
                  <a href="#childrens-privacy">Children’s Privacy</a>
                </h3>
                <p>
                  We do not aim any of our products or services directly at children under the age of 13, and we do not knowingly collect personal information
                  about children under 13.
                </p>
                <h3 id="your-rights-and-controlling-your-personal-information" className="">
                  <a href="#your-rights-and-controlling-your-personal-information">Your Rights and Controlling Your Personal Information</a>
                </h3>
                <p>
                  You always retain the right to withhold personal information from us, with the understanding that your experience of our website may be
                  affected. We will not discriminate against you for exercising any of your rights over your personal information. If you do provide us with
                  personal information you understand that we will collect, hold, use and disclose it in accordance with this privacy policy. You retain the
                  right to request details of any personal information we hold about you.
                </p>
                <p>
                  If we receive personal information about you from a third party, we will protect it as set out in this privacy policy. If you are a third
                  party providing personal information about somebody else, you represent and warrant that you have such person’s consent to provide the
                  personal information to us.
                </p>
                <p>
                  If you have previously agreed to us using your personal information for direct marketing purposes, you may change your mind at any time. We
                  will provide you with the ability to unsubscribe from our email-database or opt out of communications. Please be aware we may need to request
                  specific information from you to help us confirm your identity.
                </p>
                <p>
                  If you believe that any information we hold about you is inaccurate, out of date, incomplete, irrelevant, or misleading, please contact us
                  using the details provided in this privacy policy. We will take reasonable steps to correct any information found to be inaccurate,
                  incomplete, misleading, or out of date.
                </p>
                <p>
                  If you believe that we have breached a relevant data protection law and wish to make a complaint, please contact us using the details below
                  and provide us with full details of the alleged breach. We will promptly investigate your complaint and respond to you, in writing, setting
                  out the outcome of our investigation and the steps we will take to deal with your complaint. You also have the right to contact a regulatory
                  body or data protection authority in relation to your complaint.
                </p>
                <h3 id="use-of-cookies" className="">
                  <a href="#use-of-cookies">Use of Cookies</a>
                </h3>
                <p>
                  We use &ldquo;cookies&rdquo; to collect information about you and your activity across our site. A cookie is a small piece of data that our
                  website stores on your computer, and accesses each time you visit, so we can understand how you use our site. This helps us serve you content
                  based on preferences you have specified.
                </p>
                <h3 id="limits-of-our-policy" className="">
                  <a href="#limits-of-our-policy">Limits of Our Policy</a>
                </h3>
                <p>
                  Our website may link to external sites that are not operated by us. Please be aware that we have no control over the content and policies of
                  those sites, and cannot accept responsibility or liability for their respective privacy practices.
                </p>
                <h3 id="changes-to-this-policy" className="">
                  <a href="#changes-to-this-policy">Changes to This Policy</a>
                </h3>
                <p>
                  At our discretion, we may change our privacy policy to reflect updates to our business processes, current acceptable practices, or legislative
                  or regulatory changes. If we decide to change this privacy policy, we will post the changes here at the same link by which you are accessing
                  this privacy policy.
                </p>
                <h3 id="connecting-google-accounts" className="">
                  <a href="#connecting-google-accounts">Connecting Google Accounts</a>
                </h3>
                <div>
                  You can connect your Google Account to DataLang to access your Google Drive and Google Sheets data. We will only access the data you select.
                  The following permissions are required:
                  <ul>
                    <li>/auth/drive.metadata.readonly: View metadata for files in your Google Drive</li>
                    <li>/auth/spreadsheets.readonly: View your Google Spreadsheets data that you select</li>
                  </ul>
                </div>
                <h3 id="connecting-notion-accounts" className="">
                  <a href="#connecting-notion-accounts">Connecting Notion Accounts</a>
                </h3>
                <div>
                  You can connect your Notion Account to DataLang to access your Notion data. We will only access the data you select. DataLang only reads your
                  <ul>
                    <li>Notion workspace pages</li>
                    <li>Notion pages content</li>
                  </ul>
                </div>
                <h3 id="google-workspace-apis" className="">
                  <a href="#google-workspace-apis">Google Workspace APIs</a>
                </h3>
                <p>
                  <b>DataLang</b> does not use data obtained through <b>Google Workspace APIs</b> to develop, improve, or train generalized AI and/or ML models.
                  Your data is used solely for the purposes of creating an{" "}
                  <a href="https://platform.openai.com/docs/assistants/overview" target="_blank" rel="noreferrer">
                    OpenAI GPT Assistant
                  </a>{" "}
                  with your data as its <b>"Knowledge"</b>, but it does not train any AI and/or ML models. Learn more about how GPT assistants do not use your
                  data to train their models on the following link:{" "}
                  <a href="https://platform.openai.com/docs/models/default-usage-policies-by-endpoint" target="_blank" rel="noreferrer">
                    Default usage policies by endpoint
                  </a>
                  .
                </p>
                <h3 id="account-and-user-deletion" className="">
                  <a href="#account-and-user-deletion">Account and User Deletion</a>
                </h3>
                <p>
                  Users can delete their account and user profile at any time by navigating to the following URLs: <b>/my-account</b> and/or <b>/my-profile</b>.
                  If the user deleting the profile is the owner of the account, the entire account is deleted. In such cases, if the tenant/account has an
                  active subscription, it's automatically cancelled. All tenant data is instantly deleted, see <a href="#data-retention">Data Retention</a> for
                  more information.
                </p>
                <h3 id="data-retention" className="">
                  <a href="#data-retention">Data Retention</a>
                </h3>
                <p>
                  User data exists for as long as the user account is active. Users can delete their data at any time, and it is automatically deleted once they
                  delete their account. This includes any resources created manually or pulled by <b>third-party APIs</b>{" "}
                  <i>(Google APIs, Notion API, Snowflake API, and HubSpot API)</i>, <b>events</b> and <b>logs</b>, and all <b>user and account details</b>. We
                  ensure that deleted data is not recoverable to maintain the privacy and security of our users.
                </p>

                <h3 id="data-synchronization" className="">
                  <a href="#data-synchronization">Data Synchronization</a>
                </h3>
                <p>
                  DataLang syncs data only under specific conditions and never performs background jobs to sync data. The encrypted credentials are used only in
                  the following scenarios retrieving data from the source:
                </p>
                <ul>
                  <li>
                    <b>/chat-with/:source</b> for demos, which are deleted within 24 hours.
                  </li>
                  <li>
                    <b>/app/:account/data/sources/:id/views/new</b> when you click "Test" or "Create".
                  </li>
                  <li>
                    <b>/app/:account/data/sources/:id/views/:view</b> when you click on "Test" or "Save".
                  </li>
                </ul>
                <p>At no other time are the encrypted credentials used.</p>

                <h3 id="contact" className="">
                  <a href="#contact">Contact</a>
                </h3>
                <p>For any questions or concerns regarding your privacy, you may contact us using the following details:</p>
                <p>
                  Alexandro Martinez
                  <br />
                  hello@datalang.io
                </p>
              </div>
            </div>
          </div>
        </div>
        <FooterBlock />
      </div>
    </div>
  );
}
